import React, { useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  useTheme,
  Stack,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Search as SearchIcon,
} from "@mui/icons-material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useColorMode } from "../../../context/ColorModeContext";

// Enable dayjs plugins for date comparison
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface VideoFiltersProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dateFrom: Dayjs | null;
  setDateFrom: (date: Dayjs | null) => void;
  dateTo: Dayjs | null;
  setDateTo: (date: Dayjs | null) => void;
  sortBy: "createdAt" | "recordedAt";
  setSortBy: (sortBy: "createdAt" | "recordedAt") => void;
  onReset: () => void;
}

export const VideoFilters: React.FC<VideoFiltersProps> = ({
  searchTerm,
  onSearchChange,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  sortBy,
  setSortBy,
}) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  // Maximum selectable date for the start date (based on end date)
  const [maxDateFrom, setMaxDateFrom] = useState<Dayjs | undefined>(dayjs());

  /**
   * @name handleDateToChange
   *
   * @description Handles changes to the end date in the date filter and
   * updates maxDateFrom to ensure start date is not after end date
   *
   * @param {Dayjs | null} newValue - New end date value
   */
  const handleDateToChange = (newValue: Dayjs | null) => {
    setDateTo(newValue);
    // If dateFrom is after the new dateTo, reset dateFrom
    if (newValue && dateFrom && dateFrom.isAfter(newValue)) {
      setDateFrom(null);
    }
    // Update maxDateFrom to be the new dateTo or today if dateTo is cleared
    setMaxDateFrom(newValue || dayjs());
  };

  const handleDateFromChange = (newValue: Dayjs | null) => {
    setDateFrom(newValue);
  };

  return (
    <Grid size={{ xs: 12 }}>
      <Stack spacing={2} width="100%">
        {/* Row 1: Search and Date filters */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          {/* Search filter */}
        <TextField
          placeholder="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          sx={{ minWidth: "500px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Date from filter */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date from"
            value={dateFrom}
            onChange={handleDateFromChange}
            maxDate={dateTo || maxDateFrom}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: "175px" },
              },
              openPickerButton: {
                onMouseDown: (e) => e.preventDefault(),
              },
            }}
          />
        </LocalizationProvider>
        {/* Date to filter */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date to"
            value={dateTo}
            onChange={handleDateToChange}
            maxDate={dayjs()}
            slotProps={{
              textField: {
                size: "small",
                sx: { width: "175px" },
              },
              openPickerButton: {
                onMouseDown: (e) => e.preventDefault(),
              },
            }}
          />
        </LocalizationProvider>
        </Box>

        {/* Row 2: Reset Filters and Sort Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            flexWrap: "wrap",
          }}
        > 
          {/* Sort by control */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
                mr: 0.5,
              }}
            >
              <SwapVertIcon sx={{ fontSize: "1.25rem" }} />
              <Typography variant="body1" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                Sort:
              </Typography>
            </Box>
          <Chip
            label="Uploaded Date"
            onClick={() => setSortBy("createdAt")}
            color={sortBy === "createdAt" ? "primary" : "default"}
            variant={sortBy === "createdAt" ? "filled" : "outlined"}
            clickable
            size="medium"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: sortBy === "createdAt" ? 600 : 400,
              borderRadius: 2,
              transition: "all 0.2s ease",
              ...(sortBy !== "createdAt" && {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                color: "text.primary",
                ...(mode === "dark" && {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: theme.palette.primary.main,
                },
              }),
            }}
          />
          <Chip
            label="Recorded Date"
            onClick={() => setSortBy("recordedAt")}
            color={sortBy === "recordedAt" ? "primary" : "default"}
            variant={sortBy === "recordedAt" ? "filled" : "outlined"}
            clickable
            size="medium"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: sortBy === "recordedAt" ? 600 : 400,
              borderRadius: 2,
              transition: "all 0.2s ease",
              ...(sortBy !== "recordedAt" && {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                color: "text.primary",
                ...(mode === "dark" && {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: theme.palette.primary.main,
                },
              }),
            }}
          />
        </Stack>
        </Box>
      </Stack>
    </Grid>
  );
};
